"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { CATALOG } from "@/lib/catalog";
import { RatingStars } from "@/components/RatingStars";
import { ProductImage } from "@/components/ProductImage";

const STORAGE_KEY = "dressingroom-saved";

export default function SavedPage() {
  const [savedIds, setSavedIds] = useState<string[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [shopper, setShopper] = useState<{ id: string; name: string } | null>(null);
  const [dbSaved, setDbSaved] = useState<any[]>([]);

  // Check auth and load saved items
  useEffect(() => {
    async function init() {
      try {
        const meRes = await fetch("/api/shopper/me");
        if (meRes.ok) {
          const meData = await meRes.json();
          if (meData?.shopper) {
            setShopper(meData.shopper);
            // Fetch saved from server
            const savedRes = await fetch("/api/shopper/saved");
            if (savedRes.ok) {
              const savedData = await savedRes.json();
              setDbSaved(savedData.saved || []);
            }
            setLoaded(true);
            return;
          }
        }
      } catch {
        // not authed
      }
      // Fallback to localStorage
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) setSavedIds(JSON.parse(saved));
      } catch {
        // ignore
      }
      setLoaded(true);
    }
    init();
  }, []);

  const savedProducts = shopper
    ? dbSaved
    : savedIds.map((id) => CATALOG.find((p) => p.id === id)).filter(Boolean);

  async function removeItem(id: string) {
    if (shopper) {
      await fetch(`/api/shopper/saved?product_id=${id}`, { method: "DELETE" });
      setDbSaved((prev) => prev.filter((p) => p.id !== id));
    } else {
      const updated = savedIds.filter((s) => s !== id);
      setSavedIds(updated);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    }
  }

  if (!loaded) return null;

  return (
    <>
      <header className="site-header">
        <Link href="/" className="site-header__brand">DressingRoom</Link>
        <nav className="site-header__nav">
          <Link href="/profile" className="site-header__link">Profile</Link>
          <Link href="/store" className="site-header__link">Stores</Link>
          <Link href="/catalog" className="site-header__link">Catalog</Link>
          <Link href="/saved" className="site-header__link site-header__link--active">Saved</Link>
          {shopper ? (
            <span className="site-header__user">{shopper.name}</span>
          ) : (
            <>
              <Link href="/login" className="site-header__link">Sign In</Link>
              <Link href="/signup" className="site-header__link site-header__link--cta">Sign Up</Link>
            </>
          )}
        </nav>
      </header>

      <div className="app-shell">
        <aside className="app-shell__left">
          <div className="filter-group">
            <h3 className="filter-group__title">Account</h3>
            <div className="filter-list">
              <Link href="/profile" className="filter-item">Profile Setup</Link>
              <Link href="/catalog" className="filter-item">Browse Catalog</Link>
              <span className="filter-item filter-item--active">Saved Looks</span>
            </div>
          </div>
        </aside>

        <main className="app-shell__main">
          <h1 className="page-title">Saved Looks</h1>
          <p className="page-subtitle">
            Products you've bookmarked for later.
          </p>

          {savedProducts.length === 0 ? (
            <div className="empty-state">
              <h3 className="empty-state__title">No Saved Items</h3>
              <p className="empty-state__text">
                Browse the catalog and save items you love. They'll appear here for quick access.
              </p>
              <Link href="/catalog" className="button button--primary">
                Browse Catalog
              </Link>
            </div>
          ) : (
            <div className="saved-grid">
              {savedProducts.map((product: any) => {
                const id = shopper ? product.id : product!.id;
                const name = shopper ? product.name : product!.name;
                const brand = shopper ? (product.brand_name || "Unknown") : product!.brand;
                const price = shopper ? Number(product.price) : product!.price;
                const rating = shopper ? Number(product.rating || 0) : product!.rating;
                return (
                  <div key={id} className="saved-card">
                    <div className="saved-card__image">
                      <ProductImage productId={id} alt={name} className="" />
                    </div>
                    <div className="saved-card__body">
                      <div className="product-card__name">{name}</div>
                      <div className="product-card__brand">{brand}</div>
                      <div className="product-card__price">${price.toFixed(2)}</div>
                      <div style={{ marginTop: "var(--sp-2)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <RatingStars rating={rating} />
                        <button
                          className="button button--sm button--secondary"
                          onClick={() => removeItem(id)}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </main>
      </div>

      <nav className="mobile-nav">
        <Link href="/profile" className="mobile-nav__btn">
          <svg className="mobile-nav__icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="10" cy="7" r="3" /><path d="M4 17c0-3 3-5 6-5s6 2 6 5" />
          </svg>
          Profile
        </Link>
        <Link href="/catalog" className="mobile-nav__btn">
          <svg className="mobile-nav__icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="3" y="3" width="6" height="6" /><rect x="11" y="3" width="6" height="6" /><rect x="3" y="11" width="6" height="6" /><rect x="11" y="11" width="6" height="6" />
          </svg>
          Catalog
        </Link>
        <Link href="/saved" className="mobile-nav__btn mobile-nav__btn--active">
          <svg className="mobile-nav__icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M5 3h10v14l-5-3-5 3V3z" />
          </svg>
          Saved
        </Link>
      </nav>
    </>
  );
}
